import { przezmorzeApi, throwMessageOnBadRequestStatus, throwMessageOnStatuses } from "../../api/api";

export type TaskEntityType = "orders" | "customers" | "calculations";

export type TaskParentType = "ORDER" | "CALCULATION" | "CUSTOMER";

export interface TaskResponse {
  readonly id: string;
  readonly description: string;
  readonly due: {
    readonly date: string; // ISO date (YYYY-MM-DD)
    readonly time?: string; // HH:mm (optional)
  };
  readonly assignedUser: string; // Email
  readonly completed?: {
    readonly at: string;
    readonly by: string; // Email
    readonly note?: string;
  };
  readonly parent: TaskParentResponse;
  readonly createdBy: string;
}

export type TaskParentResponse =
  | {
      readonly type: "ORDER";
      readonly id: string;
      readonly userFriendlyId: number;
      readonly customerId: string;
    }
  | {
      readonly type: "CALCULATION" | "CUSTOMER";
      readonly id: string;
      readonly customerId: string;
    };

export interface CreateTaskRequest {
  readonly description: string;
  readonly customerId: string;
  readonly due: {
    readonly date: string; // ISO date (YYYY-MM-DD)
    readonly time?: string; // HH:mm (optional)
  };
  readonly assignedUser: string; // Email
}

export interface UpdateTaskRequest {
  readonly description: string;
  readonly due: {
    readonly date: string; // ISO date (YYYY-MM-DD)
    readonly time?: string; // HH:mm (optional)
  };
  readonly assignedUser: string; // Email
}

export async function getTasks(showCompleted: boolean, abortSignal?: AbortSignal): Promise<TaskResponse[]> {
  const response = await przezmorzeApi.get<{ items: TaskResponse[] }>(`/tasks`, {
    params: { showCompleted },
    signal: abortSignal,
  });
  return response.data.items;
}

export async function getTasksForEntity(
  entityType: TaskEntityType,
  entityId: string,
  abortSignal?: AbortSignal,
): Promise<TaskResponse[]> {
  const response = await przezmorzeApi.get<{ items: TaskResponse[] }>(`/${entityType}/${entityId}/tasks`, {
    signal: abortSignal,
  });
  return response.data.items;
}

export async function createTask(
  entityType: TaskEntityType,
  entityId: string,
  customerId: string,
  request: Omit<CreateTaskRequest, "customerId">,
  abortSignal?: AbortSignal,
): Promise<TaskResponse> {
  try {
    const response = await przezmorzeApi.post<TaskResponse>(
      `/${entityType}/${entityId}/tasks`,
      { ...request, customerId },
      {
        signal: abortSignal,
      },
    );
    return response.data;
  } catch (error) {
    // Backend uses InvalidArgumentException for past due dates → HTTP 400
    throwMessageOnBadRequestStatus(error);
  }
}

export async function deleteTask(
  entityType: TaskEntityType,
  entityId: string,
  taskId: string,
  abortSignal?: AbortSignal,
): Promise<void> {
  try {
    await przezmorzeApi.delete<void>(`/${entityType}/${entityId}/tasks/${taskId}`, { signal: abortSignal });
  } catch (error) {
    throwMessageOnStatuses(error, [403]);
  }
}

export async function completeTask(
  entityType: TaskEntityType,
  entityId: string,
  taskId: string,
  note?: string,
  abortSignal?: AbortSignal,
): Promise<TaskResponse> {
  try {
    const response = await przezmorzeApi.post<TaskResponse>(
      `/${entityType}/${entityId}/tasks/${taskId}/complete`,
      { note },
      { signal: abortSignal },
    );
    return response.data;
  } catch (error) {
    throwMessageOnStatuses(error, [400, 409]);
  }
}

export async function updateTask(
  entityType: TaskEntityType,
  entityId: string,
  taskId: string,
  request: UpdateTaskRequest,
  abortSignal?: AbortSignal,
): Promise<TaskResponse> {
  try {
    const response = await przezmorzeApi.put<TaskResponse>(`/${entityType}/${entityId}/tasks/${taskId}`, request, {
      signal: abortSignal,
    });
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}
